import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { FiCheckCircle, FiKey, FiXCircle } from "react-icons/fi";
import { motion } from "framer-motion";
import api from "../services/api";

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");
  const [isSuccessOpen, setIsSuccessOpen] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMessage("");
    setStatus("idle");
    setIsSubmitting(true);

    try {
      const response = await api.post("/reset-password", {
        token,
        password,
        confirm_password: confirmPassword,
      });

      if (response.data?.status !== "success") {
        const msg = response.data?.message || "Failed to reset password.";
        if (msg && typeof msg === "object") {
          setMessage(Object.values(msg).flat().join(" "));
        } else {
          setMessage(msg);
        }
        setStatus("error");
        return;
      }

      setStatus("success");
      setMessage("Your password has been successfully reset. Please log in.");
      setIsSuccessOpen(true);
    } catch (error) {
      const msg = error?.response?.data?.message || "Failed to reset password.";
      if (msg && typeof msg === "object") {
        setMessage(Object.values(msg).flat().join(" "));
      } else {
        setMessage(msg);
      }
      setStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (status !== "success") {
      return;
    }
    const timer = setTimeout(() => {
      navigate("/login");
    }, 5000);
    return () => clearTimeout(timer);
  }, [navigate, status]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="min-h-screen bg-[linear-gradient(135deg,#e0f2fe_0%,#fce7f3_55%,#fed7aa_100%)] px-6 py-10"
    >
      <div className="mx-auto flex w-full max-w-[900px] flex-col gap-6">
        <header className="flex items-center gap-3 rounded-3xl border border-white/60 bg-white/90 px-6 py-4 shadow-lg shadow-slate-200/60">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[linear-gradient(135deg,var(--brand-blue)_0%,var(--brand-blue-2)_100%)] text-white">
            <FiKey />
          </div>
          <div className="text-lg font-semibold text-slate-900">
            RMC2 Emergency Care (ER)
          </div>
          <Link
            to="/login"
            className="ml-auto text-sm font-semibold text-[var(--brand-blue)] hover:text-[var(--brand-blue-2)]"
          >
            Back to Login
          </Link>
        </header>

        <div className="rounded-3xl border border-white/60 bg-white/90 p-8 shadow-lg shadow-slate-200/60">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-100 text-[var(--brand-blue)]">
              {status === "success" ? <FiCheckCircle /> : <FiXCircle />}
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
                Reset
              </p>
              <h2 className="text-2xl font-semibold text-slate-900">
                Reset Password
              </h2>
              <p className="mt-2 text-sm text-slate-500">
                Enter your new password to complete the reset.
              </p>
            </div>
          </div>

          {!token && (
            <div className="mt-6 rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-600">
              Reset token is missing.
            </div>
          )}

          {token && status !== "success" && (
            <form onSubmit={handleSubmit} className="mt-6 space-y-5">
              <div>
                <label className="text-sm font-semibold text-slate-600">
                  New Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 placeholder:text-slate-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-600">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 placeholder:text-slate-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </div>

              {message && status === "error" && (
                <div className="rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-600">
                  {message}
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full rounded-2xl bg-[var(--brand-orange)] px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-orange-200 transition hover:bg-[var(--brand-orange-dark)] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isSubmitting ? "Resetting..." : "Reset Password"}
              </button>
            </form>
          )}

          {status === "success" && (
            <p className="mt-4 text-xs text-slate-400">
              You will be redirected to login in 5 seconds.
            </p>
          )}
        </div>
      </div>

      {isSuccessOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-6 py-8">
          <div className="w-full max-w-lg rounded-3xl bg-white p-6 shadow-2xl shadow-slate-900/30">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-emerald-500">
                  Success
                </p>
                <h3 className="mt-2 text-2xl font-semibold text-slate-900">
                  Password Reset
                </h3>
                <p className="mt-2 text-sm text-slate-500">
                  {message}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsSuccessOpen(false)}
                className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-500 hover:border-slate-300 hover:text-slate-700"
              >
                Close
              </button>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setIsSuccessOpen(false);
                  navigate("/login");
                }}
                className="rounded-2xl bg-[var(--brand-orange)] px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-orange-200 transition hover:bg-[var(--brand-orange-dark)]"
              >
                Go to Login
              </button>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default ResetPassword;
